import { TFunction } from "i18next";
import { LiveDataRow } from "../models/LiveDataRow";
import { CheckItem, createCheckItem, RowMatch, RowPredicate } from "./checkItems";
import { ChecklistPosition } from "./checklistPosition";
import {
  formatChecklistValue,
  formatOdometerNumber,
  formatOdometerValue,
  odometerValue,
} from "./checklistValues";
import {
  CheckKey,
  matchCheckSpecificity,
  isCanRow,
  isFuelLevelPercentRow,
  isFuelLevelVolumeRow,
  isManualOdometerRow,
  isOdometerAdjustmentRow,
  isOdometerRow,
  isRawOdometerRow,
  isTachoCanCRow,
  isTachoD8Row,
  isTachoRdlRow,
  isTellTaleRow,
  matchesCheck,
} from "./diagnosticMatchers";
import { buildIoxDevices, IoxFieldLabels, isIoxRow } from "./ioxDevices";
import { newestOf, newestPerKey } from "./newestReading";
import { findHarnessCode } from "@/constants/HarnessCodes";
import { formatDate } from "@/hooks/metrics/useFormattedDate";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { getFirmwareValue } from "@/utilities/getFirmwareValue";

/** Decimal places of a coordinate: five are about a metre, enough to find a vehicle. */
const COORDINATE_DECIMALS = 5;

/** Readings this close together count as sent at the same moment. */
const SAME_MOMENT_MS = 60_000;

/** Below this gap (in the display unit) the two odometers count as agreeing. */
const ODOMETER_MISMATCH_TOLERANCE = 1;

/**
 * How far the odometer and the raw odometer disagree, formatted, when the vehicle sent both at
 * the same moment in the period. Sent together they should read the same; a gap means one of them
 * is off, e.g. a wrong offset set by hand. Readings far apart are not compared: the vehicle may
 * have driven in between.
 */
function odometerMismatch(
  rows: LiveDataRow[],
  fallbackRows: LiveDataRow[],
  language: string,
  decimals?: number,
): string | undefined {
  for (const source of [rows, fallbackRows]) {
    const raw = newestOf(source.filter(isRawOdometerRow), row => row.dateTime);
    const odometer = newestOf(source.filter(isOdometerRow), row => row.dateTime);
    if (!raw || !odometer) {
      continue;
    }
    const rawValue = odometerValue(raw);
    const odometerReading = odometerValue(odometer);
    if (
      !rawValue ||
      !odometerReading ||
      rawValue.unit !== odometerReading.unit ||
      Math.abs(new Date(raw.dateTime).getTime() - new Date(odometer.dateTime).getTime()) >
        SAME_MOMENT_MS
    ) {
      return undefined;
    }
    const gap = Math.abs(rawValue.value - odometerReading.value);
    return gap >= ODOMETER_MISMATCH_TOLERANCE
      ? formatOdometerNumber(gap, rawValue.unit, language, decimals)
      : undefined;
  }
  return undefined;
}

/** One titled block of the vehicle checklist. */
export interface ChecklistGroup {
  title: string;
  items: CheckItem[];
  /**
   * Marks the GPS block, which has a map switch and shares a column with the block before it: it
   * holds three lines, and a fifth column would leave the four-column layout with one stray cell.
   */
  id?: "gpsData";
}

export interface BuildChecklistGroupsInput {
  /** The selected vehicle; the firmware line is read off it rather than out of the readings. */
  device?: DeviceWithVehicleInfo | null;
  /** Readings from the live window. */
  rows: LiveDataRow[];
  /** Readings from the wider history, used where the live window has nothing. */
  fallbackRows: LiveDataRow[];
  /** The unit's last GPS fix, or null when none is known (yet). */
  position?: ChecklistPosition | null;
  /** The last reading of a few diagnostics however old, for the lines that must show a figure. */
  lastKnownRows?: LiveDataRow[];
  /** Geotab's GPS-kept odometer at the end of the period, its own line. */
  gpsOdometerRows?: LiveDataRow[];
  /** Values from before the period reported earlier than this are red rather than yellow. */
  staleBefore?: Date;
  /** When the unit first sent anything, or null when it never did or it is not known (yet). */
  firstRecordAt?: Date | null;
  t: TFunction;
  /** The language the app is running in, for the number formats. */
  language: string;
  /** Decimals on the odometer lines; the default where left out. */
  odometerDecimals?: number;
}

/**
 * The vehicle checklist: what an installer has to see reported before signing a vehicle off.
 *
 * Every line answers one question — was this value reported, and what was it — and says whether
 * the answer came from the live window or only from the history. Three lines are lists rather
 * than single values, because their count is not known in advance: the CAN protocols, the
 * tachograph lines, and the add-on devices.
 */
export function buildChecklistGroups(input: BuildChecklistGroupsInput): ChecklistGroup[] {
  const { device, t, language, staleBefore } = input;

  // Every check wants the newest reading of some diagnostic, so the readings are reduced to the
  // newest one per diagnostic first, once. A day of history is tens of thousands of readings of
  // a couple of hundred diagnostics; without this each of the two dozen checks would walk all of
  // them again, on every refresh of the live window.
  //
  // The warning lights are left out here: they have a block of their own, and their names would
  // answer the lines they warn about.
  const rows = newestPerDiagnostic(input.rows.filter(row => !isTellTaleRow(row)));
  const fallbackRows = newestPerDiagnostic(input.fallbackRows.filter(row => !isTellTaleRow(row)));

  const value = (row: LiveDataRow | undefined) => formatChecklistValue(row, language);
  const odometer = (row: LiveDataRow | undefined) =>
    formatOdometerValue(row, language, input.odometerDecimals);

  function check(label: string, match: RowMatch, format = value, boolean?: boolean): CheckItem {
    return createCheckItem({ label, rows, fallbackRows, match, format, boolean, staleBefore });
  }

  /**
   * An odometer line, which falls back to the last figure ever reported.
   *
   * @param manual - Figures entered by hand that may stand in for one the vehicle never sent.
   */
  function odometerCheck(label: string, match: RowPredicate, manual?: RowPredicate): CheckItem {
    return createCheckItem({
      label,
      rows,
      fallbackRows,
      lastKnownRows: input.lastKnownRows,
      // Only real readings reach these rows: the interpolated GPS figure comes in on its own line.
      match: manual ? row => match(row) || manual(row) : match,
      format: odometer,
      staleBefore,
      manual,
    });
  }

  /** A check whose recognition rule lives in the matchers, addressed by its key. */
  function checkFor(key: CheckKey, label: string, format = value, boolean?: boolean): CheckItem {
    return check(
      label,
      {
        predicate: row => matchesCheck(key, row),
        specificity: row => matchCheckSpecificity(key, row),
      },
      format,
      boolean,
    );
  }

  function harnessHint(row: LiveDataRow): string {
    const entry = findHarnessCode(row.value);
    if (!entry) {
      return t("liveData.checklist.harnessCodeUnknown");
    }
    return entry.partNumber
      ? t("liveData.checklist.harnessCodeHint", { use: entry.use, partNumber: entry.partNumber })
      : entry.use;
  }

  const mismatch = odometerMismatch(rows, fallbackRows, language, input.odometerDecimals);
  const withMismatch = (item: CheckItem): CheckItem =>
    mismatch && item.found ? { ...item, mismatch } : item;

  return [
    {
      title: t("liveData.checklist.general"),
      items: [
        withMismatch(odometerCheck(t("liveData.checklist.rawOdometer"), isRawOdometerRow)),
        withMismatch(
          // Manual only: the fleet check hands the interpolated GPS figure in with the readings.
          odometerCheck(t("liveData.checklist.odometer"), isOdometerRow, isManualOdometerRow),
        ),
        createCheckItem({
          label: t("liveData.checklist.gpsOdometer"),
          rows: input.gpsOdometerRows ?? [],
          fallbackRows: [],
          match: isOdometerAdjustmentRow,
          format: odometer,
        }),
        // The value is the code of the harness, not a yes or no: any code is an answer, 0 included.
        createCheckItem({
          label: t("liveData.checklist.intelligentHarness"),
          rows,
          fallbackRows,
          match: {
            predicate: row => matchesCheck("intelligentHarness", row),
            specificity: row => matchCheckSpecificity("intelligentHarness", row),
          },
          format: value,
          staleBefore,
          hint: harnessHint,
        }),
        checkFor(
          "standardHarness",
          t("liveData.checklist.standardHarness"),
          value,
          true,
        ),
        firmwareItem(t, device),
        checkFor(
          "deviceVoltage",
          t("liveData.checklist.deviceVoltage"),
        ),
        checkFor("ignition", t("liveData.checklist.ignition")),
        checkFor("vehicleSpeed", t("liveData.checklist.vehicleSpeed")),
        checkFor(
          "fmsStandardSoftwareVersion",
          t(
            "liveData.checklist.fmsStandardSoftwareVersion",
            "Generic FMS standard software version",
          ),
        ),
        ...canProtocolItems(rows, fallbackRows, t),
      ],
    },
    {
      title: t("liveData.checklist.combustion"),
      items: [
        checkFor("engineSpeed", t("liveData.checklist.engineSpeed")),
        check(t("liveData.checklist.fuelLevel"), isFuelLevelPercentRow),
        check(t("liveData.checklist.fuelLevelLiters"), isFuelLevelVolumeRow),
        checkFor("defLevel", t("liveData.checklist.defLevel")),
        checkFor(
          "fuelConsumption",
          t("liveData.checklist.fuelConsumption"),
        ),
      ],
    },
    {
      title: t("liveData.checklist.ev"),
      items: [
        checkFor("stateOfCharge", t("liveData.checklist.soc")),
        checkFor("chargingState", t("liveData.checklist.dcChargingState")),
        checkFor(
          "hvBatteryCurrent",
          t("liveData.checklist.hvBatteryCurrent"),
        ),
        checkFor(
          "hvBatteryVoltage",
          t("liveData.checklist.hvBatteryVoltage"),
        ),
        checkFor("hvBatteryPower", t("liveData.checklist.hvBatteryPower")),
        checkFor("evRange", t("liveData.checklist.evRange")),
        checkFor("cellVoltageHigh", t("liveData.checklist.cellVoltageHigh")),
        checkFor("cellVoltageLow", t("liveData.checklist.cellVoltageLow")),
        checkFor(
          "cellTemperatureHigh",
          t("liveData.checklist.cellTemperatureHigh"),
        ),
        checkFor(
          "cellTemperatureLow",
          t("liveData.checklist.cellTemperatureLow"),
        ),
      ],
    },
    {
      title: t("liveData.checklist.tachoIox"),
      items: [
        ...onlyWhenReported(check(t("liveData.checklist.tachoC"), isTachoCanCRow)),
        ...onlyWhenReported(check(t("liveData.checklist.tachoD8"), isTachoD8Row)),
        ...onlyWhenReported(check(t("liveData.checklist.tachoRdl"), isTachoRdlRow)),
        ...ioxItems(rows, fallbackRows, language, t),
      ],
    },
    {
      id: "gpsData",
      title: t("liveData.checklist.gpsData"),
      items: gpsDataItems(
        rows,
        fallbackRows,
        input.position ?? null,
        device?.activeFrom ? new Date(device.activeFrom) : null,
        input.firstRecordAt ?? null,
        language,
        t,
      ),
    },
  ];
}

/**
 * Since when the vehicle is set up and its unit sending, when the vehicle last sent anything, and
 * where and when its unit last had a GPS fix.
 */
function gpsDataItems(
  rows: LiveDataRow[],
  fallbackRows: LiveDataRow[],
  position: ChecklistPosition | null,
  activeFrom: Date | null,
  firstRecordAt: Date | null,
  language: string,
  t: TFunction,
): CheckItem[] {
  const liveRecord = newestOf(rows, row => row.dateTime);
  const lastRecord = liveRecord ?? newestOf(fallbackRows, row => row.dateTime);

  return [
    {
      label: t("liveData.checklist.activeFrom"),
      value: activeFrom ? formatDate(activeFrom, language) : "",
      found: !!activeFrom,
      fromFallback: false,
    },
    {
      label: t("liveData.checklist.firstRecord"),
      value: firstRecordAt ? formatDate(firstRecordAt, language, { seconds: true }) : "",
      found: !!firstRecordAt,
      fromFallback: false,
      dateTime: firstRecordAt ?? undefined,
    },
    {
      label: t("liveData.checklist.lastRecord"),
      value: lastRecord ? formatDate(lastRecord.dateTime, language, { seconds: true }) : "",
      found: !!lastRecord,
      fromFallback: !liveRecord && !!lastRecord,
      dateTime: lastRecord?.dateTime,
    },
    {
      label: t("liveData.checklist.lastGpsAt"),
      value: position ? formatDate(position.dateTime, language, { seconds: true }) : "",
      found: !!position,
      fromFallback: false,
      dateTime: position?.dateTime,
    },
    {
      label: t("liveData.checklist.gpsCoordinates"),
      value: position
        ? `${position.latitude.toFixed(COORDINATE_DECIMALS)}, ${position.longitude.toFixed(COORDINATE_DECIMALS)}`
        : "",
      found: !!position,
      fromFallback: false,
      dateTime: position?.dateTime,
    },
  ];
}

function newestPerDiagnostic(rows: LiveDataRow[]): LiveDataRow[] {
  return newestPerKey(
    rows,
    row => row.diagnosticId || row.diagnosticName,
    row => row.dateTime,
  );
}

function firmwareItem(t: TFunction, device: DeviceWithVehicleInfo | null | undefined): CheckItem {
  const firmware = getFirmwareValue(device);
  return {
    label: t("liveData.checklist.firmware"),
    value: firmware,
    found: firmware !== "",
    fromFallback: false,
  };
}

/**
 * One numbered line per CAN protocol the vehicle reported.
 *
 * A vehicle can speak several, and how many is exactly what the installer is checking, so this
 * is a list rather than a single line. Sorted by name so the numbering does not shuffle between
 * refreshes.
 */
function canProtocolItems(
  rows: LiveDataRow[],
  fallbackRows: LiveDataRow[],
  t: TFunction,
): CheckItem[] {
  const live = canProtocolRows(rows);
  const liveNames = new Set(live.map(row => row.diagnosticName));
  const fallback = canProtocolRows(fallbackRows).filter(row => !liveNames.has(row.diagnosticName));

  const merged = [...live, ...fallback].sort((left, right) =>
    left.diagnosticName.localeCompare(right.diagnosticName),
  );

  return merged.map((row, index) => ({
    label: `${t("liveData.checklist.canProtocol")} ${index + 1}`,
    value: row.diagnosticName,
    found: true,
    fromFallback: !liveNames.has(row.diagnosticName),
    diagnosticId: row.diagnosticId || undefined,
    dateTime: row.dateTime,
  }));
}

/**
 * The CAN protocol readings a vehicle reported, sorted by name.
 *
 * Already one row per diagnostic by the time the checklist calls it, so no second dedupe.
 */
function canProtocolRows(rows: LiveDataRow[]): LiveDataRow[] {
  return (
    rows
      // An IOX-CAN adapter is add-on hardware, not a bus protocol, but its readings carry "CAN" as
      // a word — and the number of protocols is exactly what the installer is verifying here.
      .filter(row => isCanRow(row) && !isIoxRow(row) && row.value === 1)
      .sort((left, right) => left.diagnosticName.localeCompare(right.diagnosticName))
  );
}

/**
 * The CAN protocols a vehicle reported, by name.
 *
 * Also read by the fleet-wide check, which shows the count as a column rather than a numbered
 * list.
 */
export function canProtocolNames(rows: LiveDataRow[]): string[] {
  return canProtocolRows(rows).map(row => row.diagnosticName);
}

/** One line per add-on device, or a single not-found line when the vehicle reported none. */
function ioxItems(
  rows: LiveDataRow[],
  fallbackRows: LiveDataRow[],
  language: string,
  t: TFunction,
): CheckItem[] {
  const labels: IoxFieldLabels = {
    type: t("liveData.checklist.ioxType"),
    serial: t("liveData.checklist.ioxSerial"),
    status: t("liveData.checklist.ioxStatus"),
  };
  const devices = buildIoxDevices(rows, fallbackRows, language, labels);

  if (devices.length === 0) {
    return [
      {
        label: t("liveData.checklist.ioxDevices"),
        value: "",
        found: false,
        fromFallback: false,
      },
    ];
  }

  return devices.map(device => ({ ...device, found: true }));
}

/**
 * Keeps a line out of the list when the vehicle never reported it.
 *
 * The tachograph lines are not part of every installation, and an empty "not reported" line for
 * hardware the vehicle does not have would read as a fault.
 */
function onlyWhenReported(item: CheckItem): CheckItem[] {
  return item.found ? [item] : [];
}
